import React, { useState, useRef, useEffect, useCallback } from 'react';

import '../../stylesheets/components/PostcodeSelect.scss';

const FETCHIFY_JS_URL = 'https://cc-cdn.com/generic/scripts/v1/cc_c2a.min.js';

const loadScript = (src: string): Promise<void> => {
  return new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${src}"]`)) {
      resolve();
      return;
    }
    const script = document.createElement('script');
    script.src = src;
    script.onload = () => resolve();
    script.onerror = reject;
    document.head.appendChild(script);
  });
};

const getOrCreateFetchifyInstance = (apiKey: string) => {
  if (window.cc_fetchify_instance) {
    return window.cc_fetchify_instance;
  }

  const clickToAddress = window.clickToAddress;
  if (!clickToAddress) {
    return null;
  }

  window.cc_fetchify_instance = new clickToAddress({
    accessToken: apiKey,
    domMode: 'object',
    enabledCountries: ['gbr'],
    defaultCountry: 'gbr',
    getIpLocation: false,
    countrySelector: false,
    showLogo: false,
    historyTools: false,
    texts: {
      default_placeholder: 'Start typing an address or postcode',
    },
  });

  return window.cc_fetchify_instance;
};

type FormattedAddress = {
  line_1: string;
  line_2: string;
  line_3: string;
  city: string;
  postal_code: string;
  latitude: number | null;
  longitude: number | null;
};

type PostcodeSelectProps = {
  onSelect?: (address: FormattedAddress) => void;
  fetchifyApiKey?: string;
};

const PostcodeSelect: React.FC<PostcodeSelectProps> = ({ onSelect, fetchifyApiKey }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const searchInputRef = useRef<HTMLInputElement>(null);
  const line1Ref = useRef<HTMLInputElement>(null);
  const line2Ref = useRef<HTMLInputElement>(null);
  const townRef = useRef<HTMLInputElement>(null);
  const postcodeRef = useRef<HTMLInputElement>(null);
  const isAttachedRef = useRef(false);
  const isMountedRef = useRef(false);
  const onSelectRef = useRef(onSelect);
  onSelectRef.current = onSelect;

  const detachFromFetchify = useCallback(() => {
    if (isAttachedRef.current && window.cc_fetchify_instance && searchInputRef.current) {
      try {
        window.cc_fetchify_instance.detach(searchInputRef.current);
      } catch (e) {
      }
      isAttachedRef.current = false;
    }
  }, []);

  const attachToFetchify = useCallback(() => {
    if (!fetchifyApiKey) return;

    const instance = getOrCreateFetchifyInstance(fetchifyApiKey);
    if (!instance) {
      setError('Address lookup unavailable. Please enter address manually.');
      return;
    }

    const onResultSelected = (_c2a: any, _elements: any, address: any) => {
      const formattedAddress: FormattedAddress = {
        line_1: address.line_1 || '',
        line_2: address.line_2 || '',
        line_3: address.line_3 || '',
        city: address.locality || '',
        postal_code: address.postal_code || '',
        latitude: address.latitude ? parseFloat(address.latitude) : null,
        longitude: address.longitude ? parseFloat(address.longitude) : null,
      };

      onSelectRef.current?.(formattedAddress);
    };

    instance.attach(
      {
        search: searchInputRef.current,
        line_1: line1Ref.current,
        line_2: line2Ref.current,
        town: townRef.current,
        postcode: postcodeRef.current,
      },
      {
        onResultSelected,
      }
    );

    isAttachedRef.current = true;
  }, [fetchifyApiKey]);

  useEffect(() => {
    isMountedRef.current = true;

    const initFetchify = async () => {
      if (!fetchifyApiKey) {
        if (isMountedRef.current) {
          setIsLoading(false);
          setError('Address lookup is not configured. Please enter address manually.');
        }
        return;
      }

      try {
        await loadScript(FETCHIFY_JS_URL);
        if (isMountedRef.current) {
          setIsLoading(false);
          setTimeout(() => attachToFetchify(), 0);
        }
      } catch (err) {
        console.error('Failed to load Fetchify SDK:', err);
        if (isMountedRef.current) {
          setIsLoading(false);
          setError('Failed to load address lookup. Please enter address manually.');
        }
      }
    };

    initFetchify();

    return () => {
      isMountedRef.current = false;
      detachFromFetchify();
    };
  }, [fetchifyApiKey, attachToFetchify, detachFromFetchify]);

  let className = 'PostcodeSelect';
  if (error) {
    className += ' has-error';
  }

  return (
    <div className={className}>
      <div className="input-group">
        <input
          type="text"
          className="form-control"
          placeholder="Start typing an address or postcode"
          autoComplete="off"
          id="postcode_input"
          ref={searchInputRef}
          disabled={isLoading}
        />
        {isLoading && (
          <span className="input-group-append">
            <button className="btn btn-primary" disabled>Loading...</button>
          </span>
        )}
      </div>

      {error && <p className="help-block">{error}</p>}

      <input type="hidden" ref={line1Ref} />
      <input type="hidden" ref={line2Ref} />
      <input type="hidden" ref={townRef} />
      <input type="hidden" ref={postcodeRef} />
    </div>
  );
};

export default PostcodeSelect;
